import { ActionIcon, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { IconDownload, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Id, useProofFileDownload } from '@feature/utils';

import { Proof } from './ProofTypes';

interface ProofCardProps {
  proof: Proof;
  entity: string;
  canEdit?: boolean;
  onEdit?: (proof: Proof) => void;
  onDelete?: (ownerId: Id) => void;
  isPending?: boolean;
}

export function ProofCard({ proof, entity, canEdit = false, onEdit, onDelete, isPending = false }: ProofCardProps) {
  const { t } = useTranslation();
  const { downloadFile } = useProofFileDownload();

  return (
    <Paper
      p="sm"
      role="group"
      aria-label={proof.file.title}
    >
      <Flex
        justify="space-between"
        align="flex-start"
      >
        <Stack
          gap={4}
          flex={1}
        >
          <Text
            size="sm"
            fw="500"
          >
            {proof.file.title}
          </Text>
          {proof.uploadedBy.id !== proof.owner.id && (
            <Text
              size="xs"
              c="dimmed"
            >
              {t('proof.uploadedBy')}: {proof.uploadedBy.name}
            </Text>
          )}
          <Text
            size="xs"
            c="dimmed"
          >
            {t('proof.owner')}: {proof.owner.name}
          </Text>
        </Stack>
        <Group gap="xs">
          <ActionIcon
            size="sm"
            variant="outline"
            aria-label={t('button.download')}
            onClick={() => downloadFile(proof.file.id, proof.file.title, entity)}
          >
            <IconDownload size={14} />
          </ActionIcon>
          {canEdit && (
            <>
              <ActionIcon
                size="sm"
                variant="outline"
                aria-label={t('button.edit')}
                onClick={() => onEdit?.(proof)}
                loading={isPending}
              >
                <IconEdit size={14} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="outline"
                color="red"
                aria-label={t('button.delete')}
                onClick={() => onDelete?.(proof.owner.id)}
                loading={isPending}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Flex>
    </Paper>
  );
}
